
import { GoogleGenAI, Type } from "@google/genai";
import { SRTBlock, ToneLevel, MovieContext } from '../types';

// Store the key locally in the module
let currentApiKey = process.env.API_KEY || '';
let ai: GoogleGenAI | null = null;

// Function to update API Key dynamically from UI
export const setApiKey = (key: string) => {
  currentApiKey = key;
  ai = new GoogleGenAI({ apiKey: currentApiKey });
};

if (currentApiKey) {
  setApiKey(currentApiKey);
}

const getClient = (): GoogleGenAI => {
  if (!ai) {
    if (currentApiKey) {
        ai = new GoogleGenAI({ apiKey: currentApiKey });
        return ai;
    }
    throw new Error("Chưa nhập API Key. Vui lòng nhập trong phần Cấu hình.");
  }
  return ai;
};

const getToneDescription = (level: ToneLevel): string => {
  switch (level) {
    case ToneLevel.Polite: return "Trang trọng, lịch sự, PG-13. Dùng 'Tôi/Bạn', 'Anh/Em', 'Ngài' đúng chuẩn. Không dùng từ thô tục.";
    case ToneLevel.Standard: return "Văn phong phim ảnh tiêu chuẩn. Tiếng Việt tự nhiên, cân bằng.";
    case ToneLevel.Casual: return "Thân mật, bạn bè hoặc gia đình. Chỉ dùng 'Tao/Mày' nếu quan hệ cực thân hoặc thù địch. Còn lại dùng 'Tôi/Bạn/Anh/Em/Cậu/Tớ...'";
    case ToneLevel.Street: return "Bụi bặm, đường phố, giang hồ. Dùng 'Tao/Mày', chửi thề nếu ngữ cảnh phù hợp.";
    case ToneLevel.Profane: return "R-Rated (Tục). Dịch phóng khoáng, thông minh và mượt mà. 'Fucking/fuckin' dịch linh hoạt thành 'chết tiệt', 'con mẹ nó', 'chó chết', 'vãi'. 'Fuck' (cảm thán) dịch thành 'vãi lồn', 'địt con mẹ', 'đụ má'. Hãy chọn từ bậy phù hợp nhất với cảm xúc nhân vật.";
    default: return "Tiếng Việt tự nhiên chuẩn.";
  }
};

export const verifyConnection = async (): Promise<boolean> => {
  try {
    const client = getClient();
    await client.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: 'ping',
    });
    return true;
  } catch (error) {
    console.error("API Verification Error:", error);
    return false;
  }
};

export const normalizeCustomStylePrompt = async (
    rawPrompt: string,
    modelName: string
): Promise<string> => {
    const client = getClient();
    const prompt = `
    Bạn là một chuyên gia kỹ thuật prompt cho AI. 
    Nhiệm vụ của bạn là nhận các yêu cầu cá nhân hoá văn phong dịch thuật từ người dùng và chuyển nó thành một tập hợp các chỉ dẫn (instructions) rõ ràng, súc tích và hiệu quả nhất cho model AI dịch phim.

    YÊU CẦU GỐC CỦA NGƯỜI DÙNG:
    "${rawPrompt}"

    YÊU CẦU ĐẦU RA:
    - Trả về danh sách các quy tắc dịch thuật (ví dụ: "Dịch A thành B", "Văn phong hài hước", "Ưu tiên dùng từ lóng TP.HCM").
    - Giữ nguyên các ý định cụ thể của người dùng.
    - Định dạng: Text thuần túy, liệt kê theo dòng.
    - Không thêm lời dẫn giải, chỉ trả về nội dung quy tắc.
    `;

    try {
        const response = await client.models.generateContent({
            model: modelName,
            contents: prompt,
        });
        return response.text || rawPrompt;
    } catch (error) {
        console.error("Normalize Prompt Error:", error);
        throw error;
    }
};

export const detectMovieInfoFromFilename = async (
    filename: string,
    modelName: string
): Promise<MovieContext> => {
    const client = getClient();
    
    const prompt = `
    Bạn là một trợ lý cơ sở dữ liệu phim chuyên nghiệp (giống như IMDb/TMDB).
    Nhiệm vụ của bạn là phân tích TÊN FILE phụ đề sau đây và trích xuất thông tin về bộ phim hoặc TV series đó.

    TÊN FILE: "${filename}"

    YÊU CẦU:
    1. Xác định chính xác Tên phim (Title) hoặc Tên Series + Tập (nếu là TV Show). Bỏ qua các thông số kỹ thuật (1080p, BluRay, x264...).
    2. Viết một đoạn Tóm tắt nội dung (Plot) bằng TIẾNG VIỆT khoảng 3-5 câu.
    3. Nếu không xác định được phim gì từ tên file, hãy trả về title rỗng.

    TRẢ VỀ ĐỊNH DẠNG JSON:
    { "title": "Tên phim chuẩn", "plot": "Tóm tắt tiếng Việt..." }
    `;

    try {
        const response = await client.models.generateContent({
            model: modelName,
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        title: { type: Type.STRING },
                        plot: { type: Type.STRING }
                    },
                    required: ["title", "plot"]
                }
            }
        });

        let rawText = response.text || "{}";
        rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
        return JSON.parse(rawText);
    } catch (error) {
        console.error("Filename Analysis Error:", error);
        throw error;
    }
};

export const analyzeSubtitleContext = async (
    sampleBlocks: SRTBlock[], 
    modelName: string
): Promise<MovieContext> => {
    const client = getClient();
    
    // Create a simplified text blob for analysis
    const subtitleContent = sampleBlocks.map(b => `[${b.startTime}] ${b.originalText}`).join('\n');

    const prompt = `
    Dưới đây là một phần nội dung của file phụ đề phim (đã được trích xuất mẫu từ đầu, giữa và cuối phim).
    Nhiệm vụ của bạn là đọc và phân tích nội dung này để xác định ngữ cảnh.

    HÃY TRẢ LỜI CÁC THÔNG TIN SAU BẰNG TIẾNG VIỆT:
    1. title: Dự đoán tên phim chính xác nhất có thể (Nếu không biết chắc chắn, hãy đặt một tên mô tả ngắn gọn phù hợp với nội dung).
    2. plot: Viết một đoạn tóm tắt ngắn (khoảng 3-5 câu) về nội dung, bối cảnh và không khí của phim dựa trên lời thoại.

    NỘI DUNG PHỤ ĐỀ:
    ${subtitleContent}
    `;

    try {
        const response = await client.models.generateContent({
            model: modelName, // Use the model selected by user
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        title: { type: Type.STRING },
                        plot: { type: Type.STRING }
                    },
                    required: ["title", "plot"]
                }
            }
        });

        let rawText = response.text || "{}";
        rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
        
        const result = JSON.parse(rawText);
        return {
            title: result.title || "",
            plot: result.plot || ""
        };
    } catch (error) {
        console.error("Context Analysis Error:", error);
        throw error;
    }
};

export const analyzeCharacterRelationships = async (
    title: string,
    plot: string,
    modelName: string
): Promise<string> => {
    const client = getClient();

    if (!title) throw new Error("Chưa có tên phim để phân tích.");

    const prompt = `
    Dựa trên tên phim: "${title}" và nội dung tóm tắt: "${plot}".
    
    Hãy phân tích và liệt kê các MỐI QUAN HỆ NHÂN VẬT chính trong phim này và cách XƯNG HÔ (Đại từ nhân xưng) phù hợp trong tiếng Việt.
    Dùng kiến thức của bạn về phim này (nếu là phim nổi tiếng) hoặc suy luận từ logic xã hội thông thường.

    YÊU CẦU OUTPUT:
    - Trả về danh sách text thuần túy, ngắn gọn, dễ đọc.
    - Nhóm theo các phe phái/gia đình nếu cần.
    - Ghi rõ ai gọi ai là gì (Anh/Em, Tao/Mày, Tôi/Cậu, Ngài/Thần...).
    
    VÍ DỤ OUTPUT MONG MUỐN:
    Nhóm A:
    - X và Y: Vợ chồng (Anh-Em).
    - Z và X: Kẻ thù (Tao-Mày).
    Nhóm B:
    - A và B: Đồng nghiệp (Tôi-Cậu).
    `;

    try {
        const response = await client.models.generateContent({
            model: modelName,
            contents: prompt,
        });

        return response.text || "";
    } catch (error) {
        console.error("Relationship Analysis Error:", error);
        throw error;
    }
};

// --- Single Block Re-translation ---
export const translateSingleBlock = async (
    targetBlock: SRTBlock,
    contextBlocks: SRTBlock[], // Surrounding blocks for context
    modelName: string,
    tone: ToneLevel,
    context: MovieContext,
    charRelationships: string,
    enableCensorship: boolean,
    enableMetricConversion: boolean,
    enableCleanMode: boolean,
    enableHiTranslation: boolean,
    enableLyricsTranslation: boolean,
    isSouthernStyle: boolean,
    customStyle: string = ""
): Promise<string> => {
    const client = getClient();
    const toneDesc = getToneDescription(tone);
    const movieInfo = `Movie Title: ${context.title}. Plot Summary: ${context.plot}.`;
    
    const relationshipInstruction = charRelationships.trim() 
      ? `MỐI QUAN HỆ NHÂN VẬT (Ưu tiên): ${charRelationships}`
      : '';
  
    const customStyleInstruction = customStyle.trim()
      ? `QUY TẮC CÁ NHÂN HOÁ (ƯU TIÊN TUYỆT ĐỐI - OVERRIDE MỌI LUẬT KHÁC): ${customStyle}`
      : '';

    const censorshipInstruction = enableCensorship
      ? `QUY TẮC KIỂM DUYỆT (BẮT BUỘC): Che nguyên âm các từ tục tĩu bằng dấu '*'.
         Quy tắc cụ thể:
         - "địt" -> "đ*t", "lồn" -> "l*n", "cặc" -> "c*c", "đéo" -> "đ*o", "buồi" -> "b*ồi".
         - "đụ má" -> "đ* má", "chết tiệt" -> "ch*t tiệt", "con mẹ nó" -> "con m* nó".
         - NGOẠI LỆ (KHÔNG CHE): "chết" (nghĩa đen), "giết", "tự sát", "khốn nạn".`
      : '';
  
    const metricInstruction = enableMetricConversion
      ? `ĐƠN VỊ ĐO: Chuyển đổi (mile, yard, feet...) sang hệ mét (km, m, cm) tự nhiên.`
      : '';

    let hiInstruction = "";
    if (enableHiTranslation) {
        hiInstruction = `DỊCH CHÚ THÍCH [âm thanh]: Dịch nội dung trong ngoặc.`;
    } else if (enableCleanMode) {
        hiInstruction = `CHẾ ĐỘ SẠCH (Clean Mode): XÓA BỎ hoàn toàn nội dung trong ngoặc [âm thanh], (hành động). Giữ lại lời thoại chính. Nếu cả dòng chỉ là chú thích âm thanh thì trả về chuỗi rỗng.`;
    } else {
        // Fallback: Nếu không clean cũng không translate thì giữ nguyên tiếng Anh
        hiInstruction = `GIỮ NGUYÊN nội dung trong ngoặc [âm thanh].`;
    }

    const lyricsInstruction = enableLyricsTranslation
      ? `LỜI BÀI HÁT (♪): Dịch lời bài hát bay bổng, thơ ca.`
      : `LỜI BÀI HÁT (♪): GIỮ NGUYÊN TIẾNG ANH.`;

    const dialectInstruction = isSouthernStyle
      ? `VĂN PHONG VÙNG MIỀN: MIỀN NAM (HIỆN ĐẠI / TP.HCM).
         - Dùng: Muỗng (spoon), Nĩa (fork), Dĩa (plate), Chén (bowl), Ly (cup), Ba (dad).
         - TRÁNH (dùng từ phổ thông): Má -> Mẹ, Tui -> Tôi, Tụi tui -> Chúng tôi, Ổng/Cổ -> Ông ấy/Cô ấy, Vô -> Vào, Nè -> Này, Bông -> Hoa.`
      : `VĂN PHONG VÙNG MIỀN: MIỀN BẮC (HÀ NỘI).
         - Dùng: Thìa (spoon), Dĩa (fork), Đĩa (plate), Bát (bowl), Cốc (cup), Bố (dad), Mẹ (mom), Lọ (jar), Ngô (corn), Thuyền (boat), Hoa (flower).`;

    const speakerInstruction = `6. **TÊN NHÂN VẬT (Speaker Names)**: Nếu đầu câu có tên nhân vật viết hoa (dạng kịch bản "NAME:"), hãy dùng nó để hiểu ngữ cảnh ai nói, nhưng BẮT BUỘC PHẢI XÓA TÊN trong bản dịch output (VD: "MAN: Hello" -> "Xin chào").`;

    // Prepare context string
    const contextStr = contextBlocks.map(b => {
        const isTarget = b.id === targetBlock.id;
        const marker = isTarget ? ">>> DÒNG CẦN DỊCH >>> " : "";
        return `${marker}[${b.id}] ${b.originalText}`;
    }).join('\n');

    const prompt = `
    Bạn là chuyên gia dịch phụ đề. Hãy dịch lại DÒNG ĐƯỢC ĐÁNH DẤU (>>>) sang tiếng Việt dựa trên ngữ cảnh xung quanh.
    
    THÔNG TIN: ${movieInfo}
    ${relationshipInstruction}
    ${customStyleInstruction}
    TONE: ${toneDesc}
    ${dialectInstruction}
    ${censorshipInstruction}
    ${metricInstruction}
    ${hiInstruction}
    ${lyricsInstruction}

    QUY TẮC QUAN TRỌNG:
    1. **Hội thoại 2 người (-)**: Nếu dòng gốc có 2 gạch đầu dòng (thể hiện 2 người nói), bản dịch BẮT BUỘC phải giữ nguyên 2 dòng với 2 gạch đầu dòng tương ứng. KHÔNG ĐƯỢC GỘP.
    2. **Hội thoại đơn**: Nếu chỉ có 1 người nói, KHÔNG được thêm dấu gạch đầu dòng (-) ở đầu câu.
    3. **Ngắt dòng mềm**: Nếu dòng bị ngắt giữa chừng mà KHÔNG CÓ gạch đầu dòng, hãy ghép lại để hiểu nghĩa rồi dịch, đừng dịch từng mảnh.
    4. **Từ đệm**: Giữ nguyên (Oh, Yeah, OK).
    5. **Xử lý thẻ <i>**: Giữ nguyên vị trí thẻ.
    ${speakerInstruction}
    8. **LỖI CHÍNH TẢ & DÍNH CHỮ**: Kiểm tra kỹ. Sau dấu câu phải có dấu cách.
    
    NGỮ CẢNH & NỘI DUNG:
    ${contextStr}

    CHỈ TRẢ VỀ NỘI DUNG DỊCH CỦA DÒNG ĐƯỢC ĐÁNH DẤU. KHÔNG KÈM ID HAY GIẢI THÍCH.
    `;

    try {
        const response = await client.models.generateContent({
            model: modelName,
            contents: prompt,
            config: {
                temperature: 0.4,
            }
        });
        
        let text = response.text?.trim() || targetBlock.text;
        text = text.replace(/^["']|["']$/g, '');
        return text;
    } catch (error) {
        console.error("Single Block Translation Error:", error);
        throw error;
    }
};

export const translateBatch = async (
  blocks: SRTBlock[],
  previousContext: string[],
  nextContext: string[],
  modelName: string,
  tone: ToneLevel,
  context: MovieContext,
  charRelationships: string,
  enableCensorship: boolean,
  enableMetricConversion: boolean,
  enableCleanMode: boolean,
  enableHiTranslation: boolean,
  enableLyricsTranslation: boolean,
  isSouthernStyle: boolean,
  customStyle: string = ""
): Promise<Map<number, string>> => {
  
  const client = getClient();
  const toneDesc = getToneDescription(tone);
  const movieInfo = `Movie Title: ${context.title}. Plot Summary: ${context.plot}.`;
  
  const relationshipInstruction = charRelationships.trim() 
    ? `MỐI QUAN HỆ NHÂN VẬT (Do người dùng định nghĩa - Ưu tiên): ${charRelationships}`
    : '';

  const customStyleInstruction = customStyle.trim()
    ? `QUY TẮC CÁ NHÂN HOÁ (ƯU TIÊN CAO NHẤT - OVERRIDE MỌI LUẬT KHÁC): ${customStyle}`
    : '';

  const censorshipInstruction = enableCensorship
    ? `QUY TẮC KIỂM DUYỆT (BẮT BUỘC): Che nguyên âm các từ tục tĩu bằng dấu '*'.
       Quy tắc cụ thể:
       - "địt" -> "đ*t", "lồn" -> "l*n", "cặc" -> "c*c", "đéo" -> "đ*o", "buồi" -> "b*ồi".
       - "đụ má" -> "đ* má", "chết tiệt" -> "ch*t tiệt", "con mẹ nó" -> "con m* nó".
       - NGOẠI LỆ (KHÔNG CHE): "chết" (nghĩa đen), "giết", "tự sát", "khốn nạn".`
    : '';

  const metricInstruction = enableMetricConversion
    ? `QUY TẮC ĐƠN VỊ ĐO (BẮT BUỘC): Chuyển đổi các đơn vị đo lường Anh/Mỹ (mile, yard, foot, inch, pound, ounce, fahrenheit) sang hệ mét (km, m, cm, kg, g, độ C) thông dụng tại Việt Nam. Làm tròn số hợp lý cho tự nhiên (VD: 100 miles -> 160 km). Nếu là thành ngữ không liên quan đến đo lường thực tế thì giữ nguyên.`
    : '';

  let hiInstruction = "";
  if (enableHiTranslation) {
      hiInstruction = `CHẾ ĐỘ DỊCH CHÚ THÍCH (Hearing Impaired): Dịch nội dung trong ngoặc [] () sang tiếng Việt. Ví dụ: '[laughing]' -> '[cười]', '[music playing]' -> '[nhạc nổi lên]'.`;
  } else if (enableCleanMode) {
      hiInstruction = `CHẾ ĐỘ SẠCH (Clean Mode): XÓA BỎ hoàn toàn nội dung trong ngoặc vuông [] hoặc ngoặc đơn () mô tả âm thanh/hành động. Ví dụ: '[laughing]', '[speaking Japanese]', '[Door opens]', '("Dirty Work" playing)' -> Bỏ qua, không đưa vào kết quả. Giữ lại lời thoại chính. Nếu cả dòng chỉ là chú thích âm thanh thì trả về chuỗi rỗng.`;
  } else {
      hiInstruction = `CHẾ ĐỘ MẶC ĐỊNH: GIỮ NGUYÊN nội dung trong ngoặc [âm thanh] bằng tiếng Anh, không dịch.`;
  }

  const lyricsInstruction = enableLyricsTranslation
    ? `LỜI BÀI HÁT (♪, ♫): Hãy nhận diện các dòng chứa ký tự nhạc. Nếu đó là bài hát nổi tiếng, hãy sử dụng kiến thức của bạn để dịch lời bài hát sang tiếng Việt một cách bay bổng, có vần điệu (nếu có thể), đúng chất thơ ca (Lyrical/Poetic). Đừng dịch như văn nói hay đối thoại phim thông thường. Nếu không thể tìm thấy lời bài hát, hãy dịch bám sát ý nghĩa nhưng dùng từ ngữ trau chuốt hơn.`
    : `LỜI BÀI HÁT (♪, ♫): GIỮ NGUYÊN TIẾNG ANH. Không dịch.`;

  const dialectInstruction = isSouthernStyle
    ? `VĂN PHONG VÙNG MIỀN (QUAN TRỌNG): ƯU TIÊN VĂN PHONG MIỀN NAM (HIỆN ĐẠI / TP.HCM). 
       - Dùng từ vựng: "Muỗng" (spoon), "Nĩa" (fork), "Dĩa" (plate), "Chén" (bowl), "Ly" (cup), "Ba" (dad).
       - TUYỆT ĐỐI TRÁNH văn phong 'phèn' hoặc quá dân dã:
         + Dùng "Mẹ" (mom) thay vì "Má".
         + Dùng "Tôi" thay vì "Tui".
         + Dùng "Chúng tôi/chúng ta" thay vì "Tụi tui".
         + Dùng "Ông ấy/Anh ấy/Cô ấy" thay vì "Ổng/Ảnh/Cổ".
         + Dùng "Này" thay vì "Nè".
         + Dùng "Vào" (enter) thay vì "Vô".
         + Dùng "Hoa" (flower) thay vì "Bông".`
    : `VĂN PHONG VÙNG MIỀN (QUAN TRỌNG): ƯU TIÊN VĂN PHONG MIỀN BẮC (CHUẨN HÀ NỘI).
       - Từ vựng: "Thìa" (spoon), "Dĩa" (fork), "Đĩa" (plate), "Bát" (bowl), "Cốc" (cup).
       - Dùng "Bố" (dad), "Mẹ" (mom), "Lọ" (jar), "Ngô" (corn), "Thuyền" (boat), "Hoa" (flower).
       - Dùng "Tôi", "Vào", "Này" chuẩn mực.`;

  const speakerInstruction = `6. **TÊN NHÂN VẬT (Speaker Names)**: Nếu đầu câu có tên nhân vật viết hoa (dạng kịch bản "NAME:"), hãy dùng nó để hiểu ngữ cảnh ai nói, nhưng BẮT BUỘC PHẢI XÓA TÊN trong bản dịch output (VD: "MAN: Hello" -> "Xin chào"). Nếu sau khi xóa tên mà không còn lời thoại nào (VD: "TOP HAT: (CHUCKLES)") thì trả về chuỗi rỗng.`;

  const prevContextStr = previousContext.length > 0 
    ? `HỘI THOẠI NGAY TRƯỚC ĐÓ (Đã dịch - Dùng để xác định ngôi xưng hô):
       ${previousContext.join('\n')}
       --- HẾT NGỮ CẢNH CŨ ---` 
    : '';
  
  const nextContextStr = nextContext.length > 0
    ? `NỘI DUNG SẮP TỚI (Chưa dịch - Tham chiếu để hiểu mạch truyện):
       ${nextContext.join('\n')}
       --- HẾT NGỮ CẢNH TƯƠNG LAI ---`
    : '';

  const systemInstruction = `
    Bạn là một chuyên gia dịch phụ đề phim từ Tiếng Anh sang Tiếng Việt.
    
    THÔNG TIN NGỮ CẢNH:
    ${movieInfo}
    ${relationshipInstruction}
    ${customStyleInstruction}
    
    YÊU CẦU VĂN PHONG (TONE): ${toneDesc}

    ${dialectInstruction}
    ${censorshipInstruction}
    ${metricInstruction}
    ${hiInstruction}
    ${lyricsInstruction}

    CHỈ DẪN QUAN TRỌNG BẢO ĐỊNH DẠNG & CẤU TRÚC (BẮT BUỘC):
    1. **Xử lý thẻ HTML (<i>, <b>, <u>)**:
       - Các thẻ này HOÀN TOÀN VÔ HÌNH về mặt ngữ pháp.
       - Ví dụ: "<i>Look, first order of business,</i> <i>find a job.</i>" -> Phải được hiểu là một câu liền mạch: "Look, first order of business, find a job."
       - Dịch toàn bộ câu cho hoàn chỉnh nghĩa, sau đó mới đặt lại thẻ vào vị trí tương ứng trong tiếng Việt. 
       - KHÔNG được dịch rời rạc từng vế trong thẻ.

    2. **Xử lý Ngắt dòng & Hội thoại (NGHIÊM TRỌNG)**:
       - **THOẠI KÉP (DUAL DIALOGUE)**: Nếu 1 block có 2 dòng thoại của 2 người khác nhau (thường bắt đầu bằng dấu "-"), BẮT BUỘC phải trả về 2 dòng riêng biệt tương ứng, không được gộp.
         Input: 
         - How are you?
         - I'm fine.
         Output (ĐÚNG):
         - Bạn khỏe không?
         - Tôi ổn.
         Output (SAI):
         - Bạn khỏe không? Tôi ổn.
         
       - **Ngắt dòng mềm**: Nếu chỉ có 1 người nói nhưng bị ngắt dòng, hãy ghép lại thành câu hoàn chỉnh.
       
       - **QUY TẮC DẤU GẠCH (-)**: Nếu block chỉ có 1 người nói (không phải thoại kép), TUYỆT ĐỐI KHÔNG thêm dấu "-" ở đầu dòng. Nếu sau khi dịch/làm sạch chú thích mà dòng trở nên vô nghĩa hoặc chỉ còn dấu "-" thì hãy trả về chuỗi rỗng.

    3. **Ngữ cảnh & Vai vế**:
       - Đọc kỹ "HỘI THOẠI NGAY TRƯỚC ĐÓ" và "NỘI DUNG SẮP TỚI".
       - Phân tích các thẻ trong ngoặc như [Name], [Dad] để biết ai đang nói trước khi quyết định xóa chúng (nếu ở chế độ Clean Mode).
       - TUÂN THỦ "MỐI QUAN HỆ NHÂN VẬT" nếu có.

    4. **Tự nhiên**: Dịch theo ý nghĩa và cảm xúc, không dịch từng từ (word-for-word).

    5. **TỪ ĐỆM (FILLER WORDS)**:
       - GIỮ NGUYÊN các từ cảm thán tiếng Anh vô thưởng vô phạt như: "Oh", "Huh", "Yeah", "Okay", "OK", "Hey", "Hmm".
       - KHÔNG dịch chúng sang "Ồ", "Hả", "Vâng", "Được", "Này".

    ${speakerInstruction}

    7. **Định dạng Output**: Chỉ trả về JSON array thuần túy. [{ "id": number, "translation": string }]
    
    8. **LỖI CHÍNH TẢ & DÍNH CHỮ**: 
       - Kiểm tra kỹ lỗi dính từ sau dấu câu.
       - SAU dấu chấm (.), phẩy (,), chấm hỏi (?), chấm than (!) PHẢI CÓ DẤU CÁCH nếu có từ tiếp theo.
  `;

  const inputPayload = blocks.map(b => ({
    id: b.id,
    text: b.originalText
  }));

  // RETRY LOOP FOR 429 ERRORS
  let attempt = 0;
  const MAX_ATTEMPTS = 6;
  let delayMs = 4000; // Start with 4 seconds to be safe with 15 RPM

  while (attempt < MAX_ATTEMPTS) {
    try {
        const response = await client.models.generateContent({
        model: modelName,
        contents: `
        ${prevContextStr}

        ${nextContextStr}
        
        DỊCH CÁC DÒNG SAU SANG TIẾNG VIỆT (Chú ý: Giữ nguyên cấu trúc thoại kép nếu có dấu -):
        ${JSON.stringify(inputPayload)}`,
        config: {
            systemInstruction: systemInstruction,
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.ARRAY,
                items: {
                    type: Type.OBJECT,
                    properties: {
                        id: { type: Type.INTEGER },
                        translation: { type: Type.STRING }
                    },
                    required: ["id", "translation"]
                }
            },
            temperature: 0.4, 
        },
        });

        let rawText = response.text || "[]";
        // Clean up potential markdown formatting
        rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim();

        const firstBracket = rawText.indexOf('[');
        const lastBracket = rawText.lastIndexOf(']');
        if (firstBracket !== -1 && lastBracket !== -1) {
            rawText = rawText.substring(firstBracket, lastBracket + 1);
        }

        const jsonResponse = JSON.parse(rawText);
        const resultMap = new Map<number, string>();
        
        if (Array.isArray(jsonResponse)) {
        jsonResponse.forEach((item: any) => {
            if (item.id && typeof item.translation === 'string') {
            resultMap.set(item.id, item.translation);
            }
        });
        }

        return resultMap;

    } catch (error: any) {
        // Handle Rate Limits (429)
        const errorStr = JSON.stringify(error) + String(error);
        const isRateLimit = errorStr.includes('429') || errorStr.includes('RESOURCE_EXHAUSTED') || errorStr.includes('quota');
        
        if (isRateLimit) {
            attempt++;
            if (attempt < MAX_ATTEMPTS) {
                console.warn(`Hit API Rate Limit (429). Waiting ${delayMs}ms before retry ${attempt}/${MAX_ATTEMPTS}...`);
                await new Promise(r => setTimeout(r, delayMs));
                delayMs *= 2; // Exponential backoff (4s -> 8s -> 16s...)
                continue;
            }
        }

        console.error("Gemini Batch Error:", error);
        throw error;
    }
  }

  throw new Error(`Failed to translate batch after ${MAX_ATTEMPTS} attempts due to rate limits.`);
};
